import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { useTranslation } from "react-i18next";
import {
  Container,
  TextField,
  CircularProgress,
  Typography,
  Button,
  Divider,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormControl,
} from '@mui/material';
import { useForm, Controller } from "react-hook-form";
import AddIcon from '@mui/icons-material/Add';
import { guidGenerator, useViewport } from '../../helpers';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import Autocomplete from '@mui/material/Autocomplete';
import { myDesignService } from '../../services';
import CheckIcon from '@mui/icons-material/Check';

const PREFIX = 'MyDesignTargets';

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  marginRight: `${PREFIX}-marginRight`,
  addTargetContainer: `${PREFIX}-addTargetContainer`,
  heading: `${PREFIX}-heading`,
  paintingAccordionBorderBottomSelected: `${PREFIX}-paintingAccordionBorderBottomSelected`,
  doubleMarginLeft: `${PREFIX}-doubleMarginLeft`,
  accordionContainer: `${PREFIX}-accordionContainer`,
  accordionDetailsContainer: `${PREFIX}-accordionDetailsContainer`,
  inputSize: `${PREFIX}-inputSize`,
  accordionDetailDisplayMod: `${PREFIX}-accordionDetailDisplayMod`,
  accordionDetailsMainContainer: `${PREFIX}-accordionDetailsMainContainer`,
  borderError: `${PREFIX}-borderError`,
  dividerColor: `${PREFIX}-dividerColor`
};

const StyledDiv = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.marginBottom}`]: {
    marginBottom: '1rem',
  },

  [`& .${classes.marginTop}`]: {
    marginTop: '1rem',
  },

  [`& .${classes.marginRight}`]: {
    marginRight: '1rem',
  },

  [`& .${classes.addTargetContainer}`]: {
    width: '100%',
    marginTop: '1rem',
    marginBottom: '1rem',
    display: 'flex',
  },

  [`& .${classes.heading}`]: {
    fontSize: theme.typography.pxToRem(18),
    flexShrink: 0,
    transform: 'translateY(13px)',
    overflow: 'hidden',
    whiteSpace: 'no-wrap',
    textOverflow: 'ellipsis',
    maxWidth: '600px',
    [theme.breakpoints.between('xs', 'md')]: {
      maxWidth: '100px',
    },
  },

  [`& .${classes.paintingAccordionBorderBottomSelected}`]: {
    borderBottom: '1px solid grey',
  },

  [`& .${classes.doubleMarginLeft}`]: {
    marginLeft: '2rem',
  },

  [`& .${classes.accordionContainer}`]: {
    marginBottom: '1rem',
    border: '1px solid grey',
    boxShadow: '0px 7px 8px -4px rgb(0 0 0 / 20%), 0px 12px 17px 2px rgb(0 0 0 / 14%), 0px 5px 22px 4px rgb(0 0 0 / 12%)',
    width: "100%",
    backgroundColor: '#363636',
  },

  [`& .${classes.accordionDetailsContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },

  [`& .${classes.inputSize}`]: {
    width: '100%',
  },

  [`& .${classes.accordionDetailDisplayMod}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.accordionDetailsMainContainer}`]: {
    width: '100%',
    display: 'flex',
    [theme.breakpoints.between('xs', 'md')]: {
      flexDirection: 'column'
    },
  },

  [`& .${classes.borderError}`]: {
    border: '1px solid red'
  },

  [`& .${classes.dividerColor}`]: {
    backgroundColor: 'rgba(211,175,95,0.6)',
  }
}));

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

const ControlledAutocomplete = ({ options = [], renderInput, getOptionLabel, onChange: ignored, control, defaultValue, name, renderOption, errors, handleTargetChange, targetIndex }) => {

  const { t } = useTranslation();
  return (
    <StyledDiv>
      <Controller
        render={({ onChange, ...props }) => (
          <Autocomplete
            options={options}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            renderInput={renderInput}
            onChange={(e, data) => {
              handleTargetChange(data, 'timezone', targetIndex);
              onChange(data)
            }
            }
            {...props}
          />
        )}
        onChange={([, data]) => data}
        defaultValue={defaultValue}
        name={name}
        control={control}
        rules={{
          validate: value => !value ? 'timezoneError' : null
        }}
      />
      {errors.hasOwnProperty(name) &&
        <FormHelperText id="timezoneRequired" error className={clsx(classes.negateMarginTop, classes.marginBottom)}>
          {t('homePage.myDesign.createForm.errors.timezone')}
        </FormHelperText>
      }
    </StyledDiv>
  );
}

const MyDesignTargets = (props) => {

  const { t } = useTranslation();
  const { width } = useViewport();
  const { register, formState: { errors }, trigger, control, setError } = useForm({
    mode: "onChange"
  });
  const [targets, setTargets] = useState([])
  const [newTargetName, setNewTargetName] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [isNameSearching, setIsNameSearching] = useState(false);
  const debouncedSearchName = useDebounce(newTargetName, 1000);
  const [isNameUsed, setIsNameUsed] = useState(null);

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  function handleSaveNext() {
    props.handleSubmit(targets);
  }

  function handleAddTargets() {
    let tmpTargets = [...targets];
    tmpTargets.push({
      guid: guidGenerator(),
      name: newTargetName,
      timezone: 'Europe/Paris',
      redirect: '',
      blinkl: {
        campaignId: '',
        providerId: '',
      },
    });
    setTargets(tmpTargets);
    setNewTargetName('');
  }

  function handleDeleteTarget(e, index) {
    e.stopPropagation();
    const tmp = [...targets];
    tmp.splice(index, 1);
    tmp.forEach((item, mappingIndex) => item.position = mappingIndex);
    setTargets(tmp);
  }

  function handleTargetChange(value, key, index, blinkl = false) {
    let tmp = [...targets];
    if (blinkl) {
      tmp[index].blinkl[key] = value;
    } else {
      tmp[index][key] = value;
    }
    setTargets(tmp);
  }

  function targetAlreadyExist() {
    let exist = false;
    targets.forEach(target => {
      if (target.label === newTargetName) {
        exist = true;
      }
    })
    return exist;
  }

  function validateTargets() {
    let areTargetsOk = true;
    targets.forEach(target => {
      if (target.timezone.length === 0) {
        areTargetsOk = false;
        setError(`${target.guid}-timezone`, {
          type: "manual",
          message: "errorTargetTimezone"
        });
      }
    })
    return areTargetsOk;
  }

  useEffect(
    () => {
      if (debouncedSearchName && debouncedSearchName.length > 0) {
        setIsNameSearching(true);
        myDesignService.checkTargetExist(props.frameId, debouncedSearchName).then(results => {
          setIsNameSearching(false);
          setIsNameUsed(results.exists);
        });
      }
    },
    [debouncedSearchName]
  );

  return (
    <Container>
      <StyledDiv>
        <Typography variant="body1" className={clsx(classes.marginBottom, classes.marginTop)}>{t("homePage.myDesign.createForm.addTarget")}</Typography>
        <div className={classes.addTargetContainer}>
          <FormControl variant="outlined">
            <InputLabel htmlFor="name">{t("homePage.myDesign.createForm.targetName")}</InputLabel>
            <OutlinedInput
              name="addTargetName"
              label={t("homePage.myDesign.createForm.targetName")}
              value={newTargetName}
              onChange={(e) => setNewTargetName(e.target.value)}
              className={width > 1024 ? classes.marginRight : ''}
              variant={"outlined"}
              endAdornment={(isNameUsed !== null || isNameSearching) &&
                <InputAdornment position="end">
                  <IconButton aria-label="check if target name is available" size="large">
                    {isNameSearching ? <CircularProgress /> : isNameUsed ? <ClearIcon className={classes.redIcon} /> :
                      <CheckIcon className={classes.greenIcon} />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          {isNameUsed &&
            <FormHelperText id="targetNameUsed" error className={clsx(classes.negateMarginTop, classes.marginBottom)}>
              {t('homePage.myDesign.createForm.targetNameUsed')}
            </FormHelperText>
          }
          <IconButton
            color="primary"
            aria-label="add target"
            component="span"
            onClick={() => handleAddTargets()}
            disabled={newTargetName.length === 0 || targetAlreadyExist() || isNameUsed}
            size="large">
            <AddIcon />
          </IconButton>
        </div>
        {targetAlreadyExist() &&
          <Typography variant="body2" style={{ color: '#f44336', marginTop: '-0.5rem', marginBottom: '1rem' }}>{t("homePage.myDesign.createForm.errors.targetNameTaken")}</Typography>
        }
        {targets.map((target, index) => {
          return (
            <Accordion
              key={`painting-${target.guid}`}
              expanded={expanded === `panel${index + 1}`}
              onChange={handlePanelChange(`panel${index + 1}`)}
              className={clsx(classes.accordionContainer, Object.keys(errors).map(errorKey => errorKey.includes(target.guid) && classes.borderError))}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
                className={expanded === `panel${index + 1}` ? classes.paintingAccordionBorderBottomSelected : ""}
              >
                <Typography className={classes.heading}>{target.name}</Typography>
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  onClick={(e) => handleDeleteTarget(e, index)}
                  className={classes.doubleMarginLeft}
                  size="large">
                  <ClearIcon />
                </IconButton>
              </AccordionSummary>
              <AccordionDetails key={`target-content-${target.guid}`}>
                <div className={classes.accordionDetailDisplayMod}>
                  <div className={classes.accordionDetailsMainContainer}>
                    <div className={classes.accordionDetailsContainer}>
                      <Typography variant="h6">{t('homePage.myDesign.createForm.generalOptions')}</Typography>
                      <div className={clsx(classes.inputSize, classes.marginBottom)}>
                        <ControlledAutocomplete
                          control={control}
                          errors={errors}
                          name={`${target.guid}-timezone`}
                          options={props.listTimeZones()}
                          getOptionLabel={(option) => `${option}`}
                          renderInput={(params) => <TextField {...params} label={t('homePage.myDesign.createForm.choseTimezone')} margin="normal" />}
                          defaultValue={target.timezone ? target.timezone : null}
                          handleTargetChange={handleTargetChange}
                          targetIndex={index}
                        />
                      </div>
                      <div className={classes.inputSize}>
                        <TextField
                          name={`target-redirectUrl-${target.guid}`}
                          label={t('homePage.myDesign.createForm.redirectUrl')}
                          defaultValue={target.redirect}
                          onChange={(e) => handleTargetChange(e.target.value, "redirect", index)}
                          className={classes.marginRight}
                          fullWidth
                          {...register(`target-redirectUrl-${target.guid}`)}
                          variant={"outlined"}
                        />
                      </div>
                      <Divider light className={clsx(classes.marginBottom, classes.marginTop, classes.dividerColor)} />
                      <Typography variant="h6" className={clsx(classes.marginBottom)}>{t('homePage.myDesign.createForm.blinklOptions')}</Typography>
                      <div className={clsx(classes.inputSize, classes.marginBottom)}>
                        <TextField
                          name={`campaign-id-${target.guid}`}
                          label={t('homePage.myDesign.createForm.campaignId')}
                          defaultValue={target.blinkl.campaignId}
                          onChange={(e) => handleTargetChange(e.target.value, "campaignId", index, true)}
                          className={classes.marginRight}
                          fullWidth
                          {...register(`campaign-id-${target.guid}`)}
                          variant={"outlined"}
                        />
                      </div>
                      <div className={classes.inputSize}>
                        <TextField
                          name={`provider-id-${target.guid}`}
                          label={t('homePage.myDesign.createForm.providerId')}
                          defaultValue={target.blinkl.providerId}
                          onChange={(e) => handleTargetChange(e.target.value, "providerId", index, true)}
                          className={classes.marginRight}
                          fullWidth
                          {...register(`provider-id-${target.guid}`)}
                          variant={"outlined"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          );
        })}
        <Divider light className={clsx(classes.marginBottom, classes.marginTop)} />
        <div className={classes.stepperButtonPositions}>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              const result = await trigger();
              const customResult = await validateTargets();
              if (result && customResult) {
                handleSaveNext();
              }
            }}
            className={clsx(classes.marginLeft)}
            disabled={!(targets.length > 0) || props.loading}
          >
            {props.loading ? <CircularProgress size={28} /> : t('homePage.myDesign.createForm.create')}
          </Button>
        </div>
      </StyledDiv>
    </Container>
  );
}

export default MyDesignTargets;